import React, { useState } from 'react';
import { ArrowLeft, User, Globe, Shield, FileText, ChevronRight } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { User as UserType } from '../services/api';
import './SettingsPage.css';

interface SettingsPageProps {
    isOpen: boolean;
    onClose: () => void;
    currentLanguage: string;
    onLanguageChange: (languageCode: string) => void;
    user?: UserType | null;
}

type SettingsTab = 'general' | 'privacy' | 'terms';

const SettingsPage: React.FC<SettingsPageProps> = ({
    isOpen,
    onClose,
    currentLanguage,
    onLanguageChange,
    user,
}) => {
    const { t } = useTranslation(currentLanguage);
    const [activeTab, setActiveTab] = useState<SettingsTab>('general');

    if (!isOpen) return null;

    const menuItems = [
        { id: 'general', icon: User, label: t('settingsGeneral') || 'General' },
    ];

    const legalItems = [
        { id: 'privacy', icon: Shield, label: t('privacyPolicy') || 'Privacy Policy' },
        { id: 'terms', icon: FileText, label: t('termsOfService') || 'Terms of Service' },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'general':
                return (
                    <div className="settings-page-section">
                        <h2 className="settings-page-title">{t('settingsProfile') || 'Profile'}</h2>
                        <div className="settings-card">
                            <div className="settings-profile-row">
                                <div className="settings-avatar-large">
                                    {user?.avatar ? (
                                        <img src={user.avatar} alt={user.name || 'User'} referrerPolicy="no-referrer" />
                                    ) : (
                                        <span>{user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}</span>
                                    )}
                                </div>
                                <div className="settings-profile-info-large">
                                    <div className="settings-profile-name-large">{user?.name || t('user') || 'User'}</div>
                                    <div className="settings-profile-email-large">{user?.email || ''}</div>
                                    <div className="settings-plan-badge">{t('freePlan') || 'Free Plan'}</div>
                                </div>
                            </div>
                        </div>

                        <h2 className="settings-page-title mt-8">{t('language') || 'Language'}</h2>
                        <div className="settings-card">
                            <div className="settings-item-row">
                                <div className="settings-label-group">
                                    <Globe size={20} className="text-gray-500" />
                                    <span>{t('chooseLanguage') || 'Display Language'}</span>
                                </div>
                                <select
                                    value={currentLanguage}
                                    onChange={(e) => onLanguageChange(e.target.value)}
                                    className="settings-select-large"
                                >
                                    <option value="en-US">English</option>
                                    <option value="zh-CN">简体中文</option>
                                    <option value="zh-TW">繁體中文</option>
                                    <option value="ja-JP">日本語</option>
                                    <option value="ko-KR">한국어</option>
                                    <option value="de-DE">Deutsch</option>
                                    <option value="fr-FR">Français</option>
                                    <option value="es-ES">Español</option>
                                    <option value="pt-BR">Português</option>
                                    <option value="ru-RU">Русский</option>
                                    <option value="ar-SA">العربية</option>
                                    <option value="hi-IN">हिन्दी</option>
                                </select>
                            </div>
                        </div>
                    </div>
                );

            case 'privacy':
                return (
                    <div className="settings-page-section max-w-2xl">
                        <h2 className="settings-page-title">{t('privacyPolicy') || 'Privacy Policy'}</h2>
                        <div className="settings-card p-8 prose prose-sm max-w-none text-gray-600 leading-relaxed overflow-y-auto max-h-[70vh]">
                            <p className="mb-6 font-medium">Last Updated: January 2026</p>

                            <div className="space-y-6">
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Information Collection Summary</h3>
                                    <p>
                                        We ("LawHelper") value your privacy. This policy describes how we collect, use, and protect your information when using our Services.
                                    </p>
                                    <div className="mt-4">
                                        <p className="font-medium mb-2">Key Data Points:</p>
                                        <ul className="list-disc ml-5 space-y-1">
                                            <li><strong>Information You Provide</strong>: Registration details (name, email), payment data (processed securely by Stripe), and User Content (documents, queries).</li>
                                            <li><strong>Automated Collection</strong>: Device info, IP address, browser type, and usage data (timestamps, clickstreams).</li>
                                            <li><strong>Sensitive Info</strong>: We do NOT process sensitive personal information such as race, religion, or health status.</li>
                                        </ul>
                                    </div>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">2. How We Process Your Information</h3>
                                    <p>We process your information based on legal grounds including contract performance, legal obligations, and legitimate business interests:</p>
                                    <ul className="list-disc ml-5 mt-2 space-y-1">
                                        <li><strong>Service Delivery</strong>: Managing accounts, authentication, processing payments.</li>
                                        <li><strong>Improvement</strong>: Analyzing usage trends to optimize AI performance (without using your confidential data for general training).</li>
                                        <li><strong>Communication</strong>: Sending administrative notices, security alerts, and (with consent) marketing.</li>
                                        <li><strong>Security & Compliance</strong>: Fraud detection, securing system integrity, responding to legal requests.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Sharing Your Information</h3>
                                    <p>We do NOT sell your personal information. We may share data in these contexts:</p>
                                    <ul className="list-disc ml-5 mt-2 space-y-1">
                                        <li><strong>Service Providers</strong>: With essential third parties (e.g., AWS hosting, Stripe payments, email services) under strict confidentiality agreements.</li>
                                        <li><strong>Legal Obligations</strong>: If required by a subpoena, court order, or applicable law.</li>
                                        <li><strong>Business Transfers</strong>: In connection with a merger, sale of assets, or acquisition.</li>
                                        <li><strong>Consent</strong>: When you explicitly authorize us to share.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Data Security</h3>
                                    <p>We implement industry-standard security measures:</p>
                                    <ul className="list-disc ml-5 mt-2 space-y-1">
                                        <li><strong>Encryption</strong>: TLS for data in transit and AES-256 for data at rest.</li>
                                        <li><strong>Access Control</strong>: Strict employee access management.</li>
                                    </ul>
                                    <p className="mt-2 italic"><strong>Disclaimer</strong>: While we strive for security, no internet transmission is 100% secure. Please keep your credentials safe.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">5. Your Privacy Rights</h3>
                                    <p>Depending on your location (e.g., GDPR/CCPA/PIPL), you may have rights to:</p>
                                    <ul className="list-disc ml-5 mt-2 space-y-1">
                                        <li><strong>Access & Rectify</strong>: Review or update your personal information.</li>
                                        <li><strong>Erasure</strong>: Request deletion of your account and data.</li>
                                        <li><strong>Withdraw Consent</strong>: Opt-out of marketing or revoke specific processing consents.</li>
                                        <li><strong>Do-Not-Track</strong>: We currently do not respond to DNT browser signals.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">6. Cookies and Tracking</h3>
                                    <p>We use cookies to maintain session state, analyze traffic, and personalize your experience. You can refuse cookies via browser settings, though this may affect some Service functionality.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">7. Data Retention</h3>
                                    <p><strong>Retention Period</strong>: We keep your information only as long as necessary.</p>
                                    <ul className="list-disc ml-5 mt-2 space-y-1">
                                        <li><strong>Active Accounts</strong>: Account data is retained while active.</li>
                                        <li><strong>Termination</strong>: Upon deletion request, we will delete or anonymize your data within a reasonable timeframe, unless legal retention (e.g., tax logs) is required.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">8. Minors</h3>
                                    <p>We do not knowingly assist or collect data from individuals under 18. By using the Services, you represent you are at least 18. If we verify accidental collection from a minor, we will delete it immediately.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">9. International Transfers</h3>
                                    <p>LawHelper is based in Dubai, UAE, while our servers may be located in the United States. By using the Services, you consent to the transfer and processing of your data in the US, which is necessary for performing our contract with you.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">10. Updates to this Policy</h3>
                                    <p>We may update this policy periodically. Material changes will be notified via email or site notice. Continued use implies acceptance of the updated policy.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">11. Contact Us & Data Requests</h3>
                                    <p>To exercise your rights or for privacy inquiries, contact:</p>
                                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                                        <p className="font-bold">LawHelper Inc.</p>
                                        <p>Privacy Team: <a href="mailto:privacy@lawhelper.ai" className="text-indigo-600 hover:underline">privacy@lawhelper.ai</a></p>
                                        <p>Address: Dubai, United Arab Emirates</p>
                                        <p className="mt-2 italic">To request review, update, or deletion of your data, please email us directly.</p>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                );

            case 'terms':
                return (
                    <div className="settings-page-section max-w-2xl">
                        <h2 className="settings-page-title">{t('termsOfService') || 'Terms of Service'}</h2>
                        <div className="settings-card p-8 prose prose-sm max-w-none text-gray-600 leading-relaxed overflow-y-auto max-h-[70vh]">
                            <p className="mb-6 font-medium">Effective Date: January 2026</p>

                            <div className="space-y-6">
                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Agreement to Terms</h3>
                                    <p>
                                        We are LawHelper ("Company," "we," "us," "our"). We operate the website https://lawhelper.ai (the "Site"), as well as any other related products and services (collectively, the "Services").
                                    </p>
                                    <p className="mt-2">
                                        These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you"), and LawHelper, concerning your access to and use of the Services. By accessing the Services, you agree that you have read, understood, and agreed to be bound by all of these Legal Terms. <strong>IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.</strong>
                                    </p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Services and AI Disclaimer</h3>
                                    <p className="font-medium mb-2">Scope:</p>
                                    <p>LawHelper is an AI-powered legal research and drafting assistant. The Services are not tailored to comply with industry-specific regulations (e.g., HIPAA), so please do not use the Services for such sensitive data.</p>

                                    <div className="mt-4 p-4 bg-orange-50 border-l-4 border-orange-400">
                                        <p className="font-bold text-orange-800 mb-2 underline">CRITICAL DISCLAIMER:</p>
                                        <ul className="list-disc ml-5 space-y-1 text-orange-900">
                                            <li><strong>Not Legal Advice</strong>: Information provided by the Services is for reference only and DOES NOT constitute legal advice or opinion. No attorney-client relationship is formed.</li>
                                            <li><strong>AI Limitations</strong>: AI-generated content may contain errors, hallucinations, or outdated information.</li>
                                            <li><strong>Verification</strong>: You are solely responsible for verifying all information provided by the AI. We are not liable for any loss or damage arising from your reliance on AI-generated content.</li>
                                            <li><strong>Consult a Professional</strong>: For specific legal matters, you must consult with a licensed attorney.</li>
                                        </ul>
                                    </div>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Intellectual Property Rights</h3>
                                    <p className="font-medium mb-1">Our Intellectual Property:</p>
                                    <p>
                                        We are the owner or the licensee of all intellectual property rights in our Services, including source code, databases, software, website designs, trademarks, and logos. You may not exploit any part of the Services for commercial purposes without our express written permission.
                                    </p>
                                    <p className="font-medium mt-3 mb-1">Your Content:</p>
                                    <ul className="list-disc ml-5 space-y-1">
                                        <li><strong>Ownership</strong>: You retain full ownership of the documents, queries, and data you upload ("User Content").</li>
                                        <li><strong>License</strong>: You grant us a limited, non-exclusive license to access, process, and analyze your User Content solely to provide the Services to you.</li>
                                        <li><strong>Privacy</strong>: We will NOT use your confidential User Content to train our general AI models unless you explicitly provide consent.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">4. User Representations</h3>
                                    <p>By using the Services, you represent and warrant that:</p>
                                    <ul className="list-disc ml-5 space-y-1">
                                        <li>All registration information you submit is true, accurate, current, and complete.</li>
                                        <li>You have the legal capacity and are at least 18 years old.</li>
                                        <li>You will not access the Services through automated or non-human means (bots, scripts).</li>
                                        <li>Your use of the Services will not violate any applicable law or regulation.</li>
                                    </ul>
                                    <p className="mt-2"><strong>Security</strong>: You are responsible for keeping your password confidential and for all use of your account.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">5. Prohibited Activities</h3>
                                    <p>You may not use the Services for any purpose other than that for which we make the Services available. You agree not to:</p>
                                    <ul className="list-disc ml-5 space-y-1">
                                        <li>Systematically retrieve data to create a collection or database.</li>
                                        <li>Trick, defraud, or mislead us or other users.</li>
                                        <li>Interfere with security-related features of the Services.</li>
                                        <li><strong>AI Abuse</strong>: Generate fraudulent legal documents, use for illegal purposes, or create misleading content.</li>
                                        <li>Decipher, decompile, disassemble, or reverse engineer any of the software.</li>
                                        <li>Use the Services to compete with us.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">6. Payment, Free Trial, and Cancellation</h3>
                                    <p><strong>Payment</strong>: All purchases are non-refundable found. You agree to pay all charges at the prices then in effect for your purchases. If subject to recurring charges, you consent to our charging your payment method on a recurring basis.</p>
                                    <p className="mt-2 text-sm text-gray-500 italic">Note: Refunds are handled via Creem (our payment provider) subject to their merchant terms.</p>
                                    <p className="mt-2"><strong>Free Trial</strong>: If you register for a free trial, you may be charged specifically according to the subscription terms upon the trial's conclusion unless canceled.</p>
                                    <p className="mt-2"><strong>Cancellation</strong>: You can cancel your subscription at any time via your account settings. Your cancellation will take effect at the end of the current paid term.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">7. Contribution License</h3>
                                    <p>While you retain rights to your User Content (data), if you send us suggestions, feedback, or ideas ("Submissions"), you agree that we shall own such Submissions and be entitled to their unrestricted use for any lawful purpose without acknowledgment or compensation to you.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">8. Service Management and Termination</h3>
                                    <p>We reserve the right to:</p>
                                    <ul className="list-disc ml-5 space-y-1">
                                        <li>Monitor the Services for violations of these Legal Terms.</li>
                                        <li>Take appropriate legal action against violators.</li>
                                        <li>Deny access to the Services to anyone for any reason.</li>
                                    </ul>
                                    <p className="mt-2"><strong>Termination</strong>: We may terminate or suspend your account at any time, without warning, in our sole discretion, for any breach of these Legal Terms.</p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">9. Disclaimer (AS-IS)</h3>
                                    <p className="italic underline">
                                        THE SERVICES ARE PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE ASSUME NO LIABILITY FOR ANY (1) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT/AI OUTPUTS, (2) PERSONAL INJURY OR PROPERTY DAMAGE RESULTING FROM YOUR USE OF THE SERVICES, OR (3) ANY UNAUTHORIZED ACCESS TO OUR SECURE SERVERS.
                                    </p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">10. Limitations of Liability</h3>
                                    <p className="font-bold uppercase">
                                        IN NO EVENT WILL WE BE LIABLE TO YOU FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT OR LOST DATA, ARISING FROM YOUR USE OF THE SERVICES.
                                    </p>
                                    <p className="mt-2">
                                        OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER WILL AT ALL TIMES BE LIMITED TO THE AMOUNT PAID, IF ANY, BY YOU TO US DURING THE SIX (6) MONTH PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING.
                                    </p>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">11. Dispute Resolution</h3>
                                    <p><strong>Governing Law</strong>: These Terms are governed by the laws of Dubai, United Arab Emirates.</p>
                                    <p className="mt-2 font-medium">Binding Arbitration:</p>
                                    <ul className="list-disc ml-5 space-y-1">
                                        <li>Any dispute shall be resolved by binding arbitration under the Rules of the Dubai International Arbitration Centre (DIAC).</li>
                                        <li>The arbitration will take place in Dubai, UAE.</li>
                                        <li><strong>Class Action Waiver</strong>: You agree to resolve disputes only on an individual basis and waive any right to a class action.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">12. Contact Us</h3>
                                    <p>In order to resolve a complaint regarding the Services or to receive further information, please contact us at:</p>
                                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                                        <p className="font-bold">LawHelper Inc.</p>
                                        <p>Address: Dubai, United Arab Emirates</p>
                                        <p>Email: <a href="mailto:legal@lawhelper.ai" className="text-indigo-600 hover:underline">legal@lawhelper.ai</a></p>
                                        <p>Support: <a href="mailto:support@lawhelper.ai" className="text-indigo-600 hover:underline">support@lawhelper.ai</a></p>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="settings-fullscreen-container">
            {/* 顶部导航栏 */}
            <header className="settings-header-bar">
                <div className="settings-header-left">
                    <button onClick={onClose} className="settings-back-btn">
                        <ArrowLeft size={20} />
                        <span>{t('back') || 'Back'}</span>
                    </button>
                    <h1 className="settings-header-title">{t('settings') || 'Settings'}</h1>
                </div>
            </header>

            <div className="settings-layout">
                {/* 左侧菜单 */}
                <aside className="settings-sidebar">
                    <div className="settings-menu-group">
                        <div className="settings-menu-label">{t('settings') || 'Preferences'}</div>
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id as SettingsTab)}
                                className={`settings-menu-item ${activeTab === item.id ? 'active' : ''}`}
                            >
                                <item.icon size={18} />
                                <span>{item.label}</span>
                                {activeTab === item.id && <ChevronRight size={16} className="ml-auto" />}
                            </button>
                        ))}
                    </div>

                    <div className="settings-menu-group">
                        <div className="settings-menu-label">{t('about') || 'About'}</div>
                        {legalItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id as SettingsTab)}
                                className={`settings-menu-item ${activeTab === item.id ? 'active' : ''}`}
                            >
                                <item.icon size={18} />
                                <span>{item.label}</span>
                                {activeTab === item.id && <ChevronRight size={16} className="ml-auto" />}
                            </button>
                        ))}
                    </div>

                    <div className="settings-footer-info">
                        <div className="settings-version">Lawhelper v0.1.0</div>
                        <div className="settings-copyright">© 2026 Lawhelper.ai</div>
                    </div>
                </aside>

                {/* 右侧内容 */}
                <main className="settings-main-content">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

export default SettingsPage;
